import React from "react";
import { useNavigate, NavLink } from "react-router-dom";
import "./NavBar.scss";
import { FiGrid, FiCamera, FiUser, FiSettings, FiMic } from "react-icons/fi";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButtonMui from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";

import AudioRecorder from "../AudioRecorder/Audio3second";

const CONSENT_BUNDLE_CREATION_PATH =
  "/consent_bundle/submit_treatment_plan_for_consent_bundle_creation";
const CONSENT_BUNDLE_CREATED_EVENT = "medicube:consent-bundle-created";
const TREATMENT_ID_CHECK_DELAY_MS = 4000;

const getConsentBundleCreationUrl = () => {
  const v2Base = (process.env.REACT_APP_BASE_URL_V2 || "")
    .replace(/\/+$/, "")
    .replace(/\/api\/v2$/i, "/api/v1");

  const v1Base = v2Base || "https://dev.api.medicube.health/api/v1";
  return `${v1Base}${CONSENT_BUNDLE_CREATION_PATH}`;
};

const getApiErrorMessage = (error: any) =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  "";

const getConsentBundleId = (value: any) => {
  const consentBundleId =
    value?.consent_bundle_id ??
    value?.consentBundleId ??
    value?.consent_bundle?.id ??
    value?.consentBundle?.id ??
    value?.data?.consent_bundle_id ??
    value?.data?.consentBundleId ??
    value?.data?.consent_bundle?.id ??
    value?.data?.consentBundle?.id;

  return consentBundleId == null || String(consentBundleId).trim() === ""
    ? ""
    : String(consentBundleId);
};

const NavBar: React.FC = () => {
  const navigate = useNavigate();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const [isAudioModalOpen, setIsAudioModalOpen] = React.useState(false);
  const [treatmentId, setTreatmentId] = React.useState("");

  const [isCheckingTreatmentAudio, setIsCheckingTreatmentAudio] =
    React.useState(false);
  const [isTreatmentAudioApproved, setIsTreatmentAudioApproved] =
    React.useState(false);
  const [showExistingAudioWarningModal, setShowExistingAudioWarningModal] =
    React.useState(false);
  const [audioCheckError, setAudioCheckError] = React.useState("");
  const [isCreatingConsentBundle, setIsCreatingConsentBundle] =
    React.useState(false);
  const [isConsentBundleMissing, setIsConsentBundleMissing] =
    React.useState(false);
  const shouldOpenConsentBundleAfterSaveRef = React.useRef(false);
  const knownConsentBundleIdRef = React.useRef("");

  const handleCreateConsent = () => {
    navigate("/new-consentBundle");
  };

  const handleOpenAudioModal = () => {
    shouldOpenConsentBundleAfterSaveRef.current = false;
    knownConsentBundleIdRef.current = "";
    setIsAudioModalOpen(true);
    setIsTreatmentAudioApproved(false);
    setShowExistingAudioWarningModal(false);
    setIsConsentBundleMissing(false);
    setAudioCheckError("");
  };

  const handleCloseAudioModal = React.useCallback(() => {
    shouldOpenConsentBundleAfterSaveRef.current = false;
    knownConsentBundleIdRef.current = "";
    setIsAudioModalOpen(false);
    setIsCheckingTreatmentAudio(false);
    setShowExistingAudioWarningModal(false);
    setIsTreatmentAudioApproved(false);
    setIsConsentBundleMissing(false);
    setAudioCheckError("");
  }, []);

  const hostname = window.location.hostname;
  const isDevEnvironment =
    hostname === "localhost" || hostname.includes("dev.medicube");

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logOut = () => {
    Cookies.set("token", "");
    delete axios.defaults.headers.common.Authorization;
    handleCloseUserMenu();
    window.location.replace("/");
  };

  const userQuery = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data } = await axios.get("/user", {
        headers: { Accept: "application/json" },
      });

      const u = Array.isArray(data)
        ? typeof data[1] === "object" && data[1] !== null
          ? data[1]
          : data[0]
        : data;

      return u as any;
    },
  });

  const user = userQuery.data;

  const practiceIdRaw = user?.medicube_practice_id;
  const practiceId = practiceIdRaw != null ? Number(practiceIdRaw) : undefined;

  const getPracticeData = useQuery({
    queryKey: ["practice", practiceId],
    queryFn: async () => {
      const { data } = await axios.get(`/practice/${practiceId}`);
      return data;
    },
    enabled: !!practiceId,
  });

  const practiceName = getPracticeData.data?.name;

  const tooltipTitle =
    practiceName ||
    (getPracticeData.isLoading ? "Loading practice..." : "My Account");

  const avatarSrc = user?.image_url || "/user-avatar.png";
  const fallbackLetter = (user?.first_name?.trim()?.[0] || "U").toUpperCase();

  const normalizedTreatmentId = treatmentId.trim();
  const hasTreatmentId = normalizedTreatmentId.length > 0;

  const findConsentBundleIdForTreatment = React.useCallback(
    async (planId: string) => {
      const base = (process.env.REACT_APP_BASE_URL_V2 || "").replace(
        /\/+$/,
        "",
      );

      if (!base) return "";

      const { data } = await axios.get(
        `${base}/audio-recording/get_all_audio_recordings`,
      );
      const records = Array.isArray(data)
        ? data
        : data?.audio_recordings ?? data?.recordings ?? data?.data ?? [];

      if (!Array.isArray(records)) return "";

      const matchingRecord = records.find(
        (record: any) =>
          String(record?.treatment_plan_id ?? record?.treatmentPlanId ?? "") ===
          planId,
      );

      return getConsentBundleId(matchingRecord);
    },
    [],
  );

  const handlePatientNoteSavedFromHeader = React.useCallback(
    (payload?: { response?: any }) => {
      shouldOpenConsentBundleAfterSaveRef.current = true;

      const responseConsentBundleId = getConsentBundleId(payload?.response);
      if (responseConsentBundleId) {
        knownConsentBundleIdRef.current = responseConsentBundleId;
      }
    },
    [],
  );

  const handleAudioRecorderRequestClose = React.useCallback(async () => {
    const shouldOpenConsentBundle =
      shouldOpenConsentBundleAfterSaveRef.current;
    const planId = normalizedTreatmentId;
    let consentBundleId = knownConsentBundleIdRef.current;

    handleCloseAudioModal();

    if (!shouldOpenConsentBundle) {
      navigate("/dashboard?view=transcription");
      return;
    }

    if (!consentBundleId) {
      try {
        consentBundleId = await findConsentBundleIdForTreatment(planId);
      } catch (error) {
        console.error(
          "Failed to resolve the consent bundle after saving the patient note:",
          error,
        );
      }
    }

    const params = new URLSearchParams({
      step: "1",
      transcription: "true",
      from: "header",
      patientNoteStatus: "edited",
      focus: "ai-patient-letter-generator",
    });

    if (consentBundleId) {
      params.set("consentBundleId", consentBundleId);

      try {
        const token =
          axios.defaults.headers.common.Authorization ??
          axios.defaults.headers.common.authorization;
        const { data } = await axios.get(
          `/consent_bundle/${encodeURIComponent(consentBundleId)}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: String(token) } : {}),
            },
          },
        );
        const consentBundleData = data?.consent_bundle ?? data;

        sessionStorage.setItem(
          "activeTreatmentData",
          JSON.stringify(consentBundleData),
        );
      } catch (error) {
        console.error(
          "Failed to preload the consent bundle after saving the patient note:",
          error,
        );
      }
    }

    navigate(`/new-consentBundle?${params.toString()}`);
  }, [
    findConsentBundleIdForTreatment,
    handleCloseAudioModal,
    navigate,
    normalizedTreatmentId,
  ]);

  const handleTreatmentIdChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const numericValue = event.target.value.replace(/\D/g, "");

    setTreatmentId(numericValue);
    shouldOpenConsentBundleAfterSaveRef.current = false;
    knownConsentBundleIdRef.current = "";
    setIsTreatmentAudioApproved(false);
    setShowExistingAudioWarningModal(false);
    setIsConsentBundleMissing(false);
    setAudioCheckError("");
  };

  const handleCreateConsentBundleIfMissing = React.useCallback(
    async (planId: string) => {
      setIsCreatingConsentBundle(true);
      setIsTreatmentAudioApproved(false);
      setShowExistingAudioWarningModal(false);
      setAudioCheckError("");

      try {
        const { data } = await axios.post(getConsentBundleCreationUrl(), {
          id: planId,
          mode: "consent_bundle",
        });

        const createdConsentBundleId = getConsentBundleId(data);
        if (createdConsentBundleId) {
          knownConsentBundleIdRef.current = createdConsentBundleId;
        }

        window.dispatchEvent(
          new CustomEvent(CONSENT_BUNDLE_CREATED_EVENT, {
            detail: { treatmentPlanId: planId },
          }),
        );

        setIsConsentBundleMissing(false);
        setIsTreatmentAudioApproved(true);
        setAudioCheckError("");
      } catch (error: any) {
        const message =
          getApiErrorMessage(error) ||
          "Failed to create the consent bundle. Please try again.";

        setIsConsentBundleMissing(true);
        setIsTreatmentAudioApproved(false);
        setAudioCheckError(message);
      } finally {
        setIsCreatingConsentBundle(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!isAudioModalOpen || !hasTreatmentId) {
      setIsCheckingTreatmentAudio(false);
      setIsTreatmentAudioApproved(false);
      setShowExistingAudioWarningModal(false);
      setIsConsentBundleMissing(false);
      setAudioCheckError("");
      return;
    }

    const base = (process.env.REACT_APP_BASE_URL_V2 || "").replace(/\/+$/, "");

    if (!base) {
      setAudioCheckError("REACT_APP_BASE_URL_V2 is not set.");
      setIsTreatmentAudioApproved(false);
      return;
    }

    const controller = new AbortController();
    let isCurrentRequest = true;

    setIsCheckingTreatmentAudio(true);
    setIsTreatmentAudioApproved(false);
    setShowExistingAudioWarningModal(false);
    setIsConsentBundleMissing(false);
    setAudioCheckError("");

    const timer = window.setTimeout(async () => {
      try {
        const { data } = await axios.get<
          | {
              detail?: string;
              error?: string;
              result?: boolean;
              consent_bundle_id?: string | number;
              consentBundleId?: string | number;
            }
          | boolean
        >(
          `${base}/audio-recording/check_treatment_plan_id/${encodeURIComponent(
            normalizedTreatmentId,
          )}`,
          {
            signal: controller.signal,
            validateStatus: () => true,
          },
        );

        if (!isCurrentRequest) return;

        if (data && typeof data === "object") {
          const checkedConsentBundleId = getConsentBundleId(data);
          if (checkedConsentBundleId) {
            knownConsentBundleIdRef.current = checkedConsentBundleId;
          }

          const responseMessage = data.detail || data.error || "";
          const expectedMissingConsentBundleDetail = `check_treatment_plan_id failed: Consent bundle with treatment id: ${normalizedTreatmentId} does not exist`;

          if (responseMessage.trim() === expectedMissingConsentBundleDetail) {
            setIsConsentBundleMissing(true);
            return;
          }

          if (responseMessage) {
            throw new Error(responseMessage);
          }

          if (data.result === false) {
            setShowExistingAudioWarningModal(true);
            return;
          }
        }

        setIsConsentBundleMissing(false);
        setIsTreatmentAudioApproved(true);
      } catch (error: any) {
        if (
          !isCurrentRequest ||
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error("Error checking treatment audio:", error);
        setIsTreatmentAudioApproved(false);
        setAudioCheckError(
          getApiErrorMessage(error) ||
            "Failed to check treatment ID. Please try again.",
        );
      } finally {
        if (isCurrentRequest) {
          setIsCheckingTreatmentAudio(false);
        }
      }
    }, TREATMENT_ID_CHECK_DELAY_MS);

    return () => {
      isCurrentRequest = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [
    hasTreatmentId,
    isAudioModalOpen,
    normalizedTreatmentId,
  ]);

  const isRecordDisabled =
    !hasTreatmentId ||
    isCheckingTreatmentAudio ||
    isCreatingConsentBundle ||
    !isTreatmentAudioApproved;
  const recordDisabledLabel = isCreatingConsentBundle
    ? "Creating..."
    : isCheckingTreatmentAudio
      ? "Checking..."
      : "Record";
  const recordDisabledMessage = isCreatingConsentBundle
    ? "Consent bundle creation is still in progress."
    : isCheckingTreatmentAudio
      ? "Treatment ID is being checked."
      : isConsentBundleMissing
        ? "Create a consent bundle for this Treatment ID before recording."
        : "Enter a valid Treatment ID and wait for the check to finish.";

  return (
    <>
      {isDevEnvironment && (
        <div className="mc-env-banner">Development Sandbox Environment</div>
      )}

      <header className="mc-nav-shell">
        <div className="mc-nav">
          <div className="mc-nav__top">
            <div className="mc-nav__brand">
              <img
                className="mc-nav__logo"
                src="/logo.png"
                alt="Medicube"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <nav className="mc-nav__links" aria-label="Main">
              <NavLink
                to="/welcome"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FavoriteBorderIcon className="mc-nav__icon" />
                <span className="mc-nav__label">Welcome</span>
              </NavLink>

              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiGrid className="mc-nav__icon" />
                <span className="mc-nav__label">Dashboard</span>
              </NavLink>

              <NavLink
                to="/video-gallery"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiCamera className="mc-nav__icon" />
                <span className="mc-nav__label">Video Gallery</span>
              </NavLink>

              <NavLink
                to="/templatesPagev2"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiUser className="mc-nav__icon" />
                <span className="mc-nav__label">My Consent Templates</span>
              </NavLink>

              <NavLink
                to="/my-plan"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiSettings className="mc-nav__icon" />
                <span className="mc-nav__label">My Plan</span>
              </NavLink>
            </nav>

            <div className="mc-nav__actions">
              <button
                className="mc-btn mc-btn--icon"
                onClick={handleOpenAudioModal}
                type="button"
                aria-label="Audio recording"
              >
                <FiMic />
              </button>

              <button
                className="mc-btn mc-btn--primary"
                onClick={handleCreateConsent}
                type="button"
              >
                Create consent form
              </button>

              <Tooltip title={tooltipTitle}>
                <IconButton
                  className="mc-nav__avatarBtn"
                  onClick={handleOpenUserMenu}
                  aria-controls={anchorElUser ? "user-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorElUser ? "true" : undefined}
                >
                  <Avatar className="mc-nav__avatar" alt="User" src={avatarSrc}>
                    {fallbackLetter}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <Menu
          id="user-menu"
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/mobile_uploader");
            }}
          >
            <Typography textAlign="center">Image uploader</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/tutorials");
            }}
          >
            <Typography textAlign="center">My Tutorials</Typography>
          </MenuItem>

          <MenuItem onClick={logOut}>
            <Typography textAlign="center">Logout</Typography>
          </MenuItem>
        </Menu>
      </header>

      <Dialog
        open={isAudioModalOpen}
        onClose={handleCloseAudioModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            maxHeight: "92vh",
          },
        }}
      >
        <div className="mc-audio-modal__header">
          <div className="mc-audio-modal__headerContent">
            <div>
              <h2>Audio recording</h2>
              <p>
                Record audio first, then add the Treatment ID before uploading.
              </p>
            </div>

            <div className="mc-audio-modal__treatmentField">
              <label htmlFor="audio-treatment-id">
                Treatment ID <span>*</span>
              </label>

              <input
                id="audio-treatment-id"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={treatmentId}
                onChange={handleTreatmentIdChange}
                placeholder="Add treatment ID"
                disabled={isCreatingConsentBundle}
                aria-required="true"
              />

              {!hasTreatmentId && (
                <small>
                  A valid Treatment ID is required before recording audio.
                </small>
              )}

              {hasTreatmentId &&
                isCheckingTreatmentAudio &&
                !isCreatingConsentBundle && (
                  <small style={{ color: "#475569", fontWeight: 600 }}>
                    Checking Treatment ID...
                  </small>
                )}

              {hasTreatmentId && isCreatingConsentBundle && (
                <small style={{ color: "#475569", fontWeight: 600 }}>
                  Creating consent bundle. Please wait...
                </small>
              )}

              {hasTreatmentId &&
                isConsentBundleMissing &&
                !isCreatingConsentBundle && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginTop: "6px",
                    }}
                  >
                    <small style={{ color: "#b45309", fontWeight: 600 }}>
                      No consent bundle exists for this Treatment ID.
                    </small>

                    <button
                      type="button"
                      onClick={() => {
                        void handleCreateConsentBundleIfMissing(
                          normalizedTreatmentId,
                        );
                      }}
                      style={{
                        minHeight: "32px",
                        padding: "7px 12px",
                        border: "1px solid #c2410c",
                        borderRadius: "8px",
                        background: "#e25a1b",
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Create consent bundle
                    </button>
                  </div>
                )}

              {hasTreatmentId && isTreatmentAudioApproved && (
                <small style={{ color: "#15803d", fontWeight: 600 }}>
                  Treatment ID checked. Ready to record.
                </small>
              )}

              {audioCheckError && (
                <small style={{ color: "#dc2626", fontWeight: 600 }}>
                  {audioCheckError}
                </small>
              )}
            </div>
          </div>

          <IconButtonMui
            onClick={handleCloseAudioModal}
            aria-label="Close audio recorder"
            size="small"
          >
            <CloseIcon />
          </IconButtonMui>
        </div>

        <DialogContent className="mc-audio-modal__content">
          <AudioRecorder
            consentBundleId={normalizedTreatmentId}
            treatmentPlanId={normalizedTreatmentId}
            apiBaseUrl={process.env.REACT_APP_BASE_URL_V2}
            onRequestClose={handleAudioRecorderRequestClose}
            onPatientNoteSaved={handlePatientNoteSavedFromHeader}
            uploadEnabled={hasTreatmentId && isTreatmentAudioApproved}
            recordDisabled={isRecordDisabled}
            recordDisabledLabel={recordDisabledLabel}
            recordDisabledMessage={recordDisabledMessage}
            skipTreatmentPlanCheckOnRecord
            matchConsentBundleId={false}
            uploadDisabledMessage={
              isConsentBundleMissing
                ? "Create a consent bundle for this Treatment ID before uploading."
                : hasTreatmentId
                  ? "Treatment ID is being checked. Please wait before uploading."
                  : "Add a treatment ID before uploading audio."
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={showExistingAudioWarningModal}
        onClose={() => setShowExistingAudioWarningModal(false)}
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(30, 41, 59, 0.68)",
            backdropFilter: "blur(2px)",
          },
        }}
        PaperProps={{
          sx: {
            width: "calc(100% - 32px)",
            maxWidth: "420px",
            margin: "16px",
            borderRadius: "18px",
            border: "1px solid rgba(226, 232, 240, 0.95)",
            background: "#ffffff",
            boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
            overflow: "hidden",
          },
        }}
      >
        <div
          style={{
            padding: "18px 20px 20px",
            textAlign: "center",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "38px",
              height: "38px",
              marginBottom: "12px",
              borderRadius: "999px",
              background: "#fee2e2",
              color: "#b91c1c",
              fontSize: "22px",
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            !
          </div>

          <h3
            style={{
              margin: "0 0 8px",
              fontSize: "18px",
              fontWeight: 800,
              lineHeight: 1.3,
              color: "#111827",
            }}
          >
            Existing recording found
          </h3>

          <p
            style={{
              maxWidth: "370px",
              margin: "0 auto",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: 1.55,
              color: "#5f6b7a",
              textAlign: "center",
            }}
          >
            A recording already exists for Treatment Plan ID{" "}
            {normalizedTreatmentId}. Continuing will replace the old audio,
            transcript, and patient note. Do you want to continue?
          </p>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "14px",
              padding: "10px 12px",
              border: "1px solid #d8b4fe",
              borderRadius: "11px",
              background: "#faf7ff",
              color: "#374151",
              fontSize: "12px",
              fontWeight: 700,
              lineHeight: 1.4,
              textAlign: "left",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: "#8b5cf6",
                fontSize: "15px",
                fontWeight: 900,
                lineHeight: 1,
                flexShrink: 0,
              }}
            ></span>
            <span>
              This action cannot be undone after the new recording is processed.
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              marginTop: "16px",
            }}
          >
            <button
              type="button"
              style={{
                minHeight: "36px",
                padding: "9px 14px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#f1f5f9",
                color: "#334155",
                fontSize: "13px",
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
              }}
              onClick={() => {
                setShowExistingAudioWarningModal(false);
                setIsTreatmentAudioApproved(false);
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              style={{
                minHeight: "36px",
                padding: "9px 16px",
                border: "1px solid #dc2626",
                borderRadius: "10px",
                background: "linear-gradient(180deg, #ef2b2d 0%, #dc171b 100%)",
                color: "#ffffff",
                boxShadow: "0 8px 18px rgba(220, 38, 38, 0.24)",
                fontSize: "13px",
                fontWeight: 800,
                lineHeight: 1,
                cursor: "pointer",
              }}
              onClick={() => {
                setShowExistingAudioWarningModal(false);
                setIsTreatmentAudioApproved(true);
                setAudioCheckError("");
              }}
            >
              Yes, continue
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default NavBar;
